'use client';

import { c as _c } from "react/compiler-runtime";
import type { StyleWithCssVariables } from '@repo/ui/lib/css-variables';
import { cn } from '@repo/ui/lib/utils';
import * as React from 'react';
import * as RechartsPrimitive from 'recharts';
const THEMES = [{
  name: 'dark',
  selector: '.dark'
}, {
  name: 'light',
  selector: ''
}] as const;
export type ChartConfig = { [k in string]: ({
  color?: never;
  theme: Record<(typeof THEMES)[number]['name'], string>;
} | {
  color?: string;
  theme?: never;
}) & {
  icon?: React.ComponentType;
  label?: React.ReactNode;
} };
type ChartContextProps = {
  config: ChartConfig;
};
const ChartContext = React.createContext<ChartContextProps | null>(null);
function ChartContainer(t0) {
  const $ = _c(27);
  let children;
  let className;
  let config;
  let id;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      className,
      config,
      id,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = config;
    $[4] = id;
    $[5] = props;
  } else {
    children = $[1];
    className = $[2];
    config = $[3];
    id = $[4];
    props = $[5];
  }
  const uniqueId = React.useId();
  let t1;
  if ($[6] !== id || $[7] !== uniqueId) {
    t1 = id || uniqueId.replace(/:/g, "");
    $[6] = id;
    $[7] = uniqueId;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const chartId = `chart-${t1}`;
  let t2;
  if ($[9] !== config) {
    t2 = {
      config
    };
    $[9] = config;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== className) {
    t3 = cn("flex aspect-video justify-center text-xs [&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground [&_.recharts-cartesian-grid_line[stroke='#ccc']]:stroke-border/50 [&_.recharts-curve.recharts-tooltip-cursor]:stroke-border [&_.recharts-dot[stroke='#fff']]:stroke-transparent [&_.recharts-layer]:outline-hidden [&_.recharts-polar-grid_[stroke='#ccc']]:stroke-border [&_.recharts-radial-bar-background-sector]:fill-muted [&_.recharts-rectangle.recharts-tooltip-cursor]:fill-muted [&_.recharts-reference-line_[stroke='#ccc']]:stroke-border [&_.recharts-sector]:outline-hidden [&_.recharts-sector[stroke='#fff']]:stroke-transparent [&_.recharts-surface]:outline-hidden", className);
    $[11] = className;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== chartId || $[14] !== config) {
    t4 = <ChartStyle config={config} id={chartId} data-sentry-element="ChartStyle" data-sentry-source-file="chart.tsx" />;
    $[13] = chartId;
    $[14] = config;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== children) {
    t5 = <RechartsPrimitive.ResponsiveContainer data-sentry-element="RechartsPrimitive.ResponsiveContainer" data-sentry-source-file="chart.tsx">{children}</RechartsPrimitive.ResponsiveContainer>;
    $[16] = children;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== chartId || $[19] !== props || $[20] !== t3 || $[21] !== t4 || $[22] !== t5) {
    t6 = <div className={t3} data-chart={chartId} data-slot="chart" {...props}>{t4}{t5}</div>;
    $[18] = chartId;
    $[19] = props;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== t2 || $[25] !== t6) {
    t7 = <ChartContext.Provider value={t2} data-sentry-element="ChartContext.Provider" data-sentry-component="ChartContainer" data-sentry-source-file="chart.tsx">{t6}</ChartContext.Provider>;
    $[24] = t2;
    $[25] = t6;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  return t7;
}
function useChart() {
  const context = React.useContext(ChartContext);
  if (!context) {
    throw new Error("useChart must be used within a <ChartContainer />");
  }
  return context;
}
const ChartStyle = t0 => {
  const $ = _c(7);
  const {
    config,
    id
  } = t0;
  let t1;
  if ($[0] !== config) {
    t1 = Object.entries(config).filter(_temp);
    $[0] = config;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const colorConfig = t1;
  if (!colorConfig.length) {
    return null;
  }
  let t2;
  if ($[2] !== colorConfig || $[3] !== id) {
    t2 = THEMES.map(t3 => {
      const {
        name,
        selector
      } = t3;
      return `
${selector} [data-chart=${id}] {
${colorConfig.map(t4 => {
        const [key, itemConfig] = t4;
        const color = itemConfig.theme?.[name] || itemConfig.color;
        return color ? `  --color-${key}: ${color};` : null;
      }).join("\n")}
}
`;
    }).join("\n");
    $[2] = colorConfig;
    $[3] = id;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = <style dangerouslySetInnerHTML={{
      __html: t2
    }} data-sentry-component="ChartStyle" data-sentry-source-file="chart.tsx" />;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
const ChartTooltip = RechartsPrimitive.Tooltip;
function ChartTooltipContent(t0) {
  const $ = _c(45);
  const {
    active,
    className,
    color,
    formatter,
    hideIndicator: t1,
    hideLabel: t2,
    indicator: t3,
    label,
    labelClassName,
    labelFormatter,
    labelKey,
    nameKey,
    payload
  } = t0;
  const hideIndicator = t1 === undefined ? false : t1;
  const hideLabel = t2 === undefined ? false : t2;
  const indicator = t3 === undefined ? "dot" : t3;
  const {
    config
  } = useChart();
  let t4;
  bb0: {
    if (hideLabel || !payload?.length) {
      t4 = null;
      break bb0;
    }
    const [item] = payload;
    const key = `${labelKey || item?.dataKey || item?.name || "value"}`;
    let t5;
    if ($[0] !== config || $[1] !== item || $[2] !== key) {
      t5 = getPayloadConfigFromPayload(config, item, key);
      $[0] = config;
      $[1] = item;
      $[2] = key;
      $[3] = t5;
    } else {
      t5 = $[3];
    }
    const itemConfig = t5;
    const value = !labelKey && typeof label === "string" ? config[label]?.label || label : itemConfig?.label;
    if (labelFormatter) {
      let t6;
      if ($[4] !== labelClassName) {
        t6 = cn("font-medium", labelClassName);
        $[4] = labelClassName;
        $[5] = t6;
      } else {
        t6 = $[5];
      }
      let t7;
      if ($[6] !== labelFormatter || $[7] !== payload || $[8] !== value) {
        t7 = labelFormatter(value, payload);
        $[6] = labelFormatter;
        $[7] = payload;
        $[8] = value;
        $[9] = t7;
      } else {
        t7 = $[9];
      }
      let t8;
      if ($[10] !== t6 || $[11] !== t7) {
        t8 = <div className={t6}>{t7}</div>;
        $[10] = t6;
        $[11] = t7;
        $[12] = t8;
      } else {
        t8 = $[12];
      }
      t4 = t8;
      break bb0;
    }
    if (!value) {
      t4 = null;
      break bb0;
    }
    let t6;
    if ($[13] !== labelClassName) {
      t6 = cn("font-medium", labelClassName);
      $[13] = labelClassName;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    let t7;
    if ($[15] !== t6 || $[16] !== value) {
      t7 = <div className={t6}>{value}</div>;
      $[15] = t6;
      $[16] = value;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    t4 = t7;
  }
  const tooltipLabel = t4;
  if (!active || !payload?.length) {
    return null;
  }
  const nestLabel = payload.length === 1 && indicator !== "dot";
  let t5;
  if ($[18] !== className) {
    t5 = cn("grid min-w-[8rem] items-start gap-1.5 rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-xs shadow-xl", className);
    $[18] = className;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const t6 = !nestLabel ? tooltipLabel : null;
  let t7;
  if ($[20] !== color || $[21] !== config || $[22] !== formatter || $[23] !== hideIndicator || $[24] !== indicator || $[25] !== nameKey || $[26] !== nestLabel || $[27] !== payload || $[28] !== tooltipLabel) {
    let t8;
    if ($[30] !== color || $[31] !== config || $[32] !== formatter || $[33] !== hideIndicator || $[34] !== indicator || $[35] !== nameKey || $[36] !== nestLabel || $[37] !== tooltipLabel) {
      t8 = (item_0, index) => {
        const key_0 = `${nameKey || item_0.name || item_0.dataKey || "value"}`;
        const itemConfig_0 = getPayloadConfigFromPayload(config, item_0, key_0);
        const indicatorColor = color || item_0.payload.fill || item_0.color;
        const indicatorStyle = {
          "--color-bg": indicatorColor,
          "--color-border": indicatorColor
        };
        return <div className={cn("flex w-full flex-wrap items-stretch gap-2 [&>svg]:h-2.5 [&>svg]:w-2.5 [&>svg]:text-muted-foreground", indicator === "dot" && "items-center")} key={key_0}>{formatter && item_0?.value !== undefined && item_0.name ? formatter(item_0.value, item_0.name, item_0, index, item_0.payload) : <>{itemConfig_0?.icon ? <itemConfig_0.icon /> : !hideIndicator && <div className={cn("shrink-0 rounded-[2px] border-(--color-border) bg-(--color-bg)", {
              "h-2.5 w-2.5": indicator === "dot",
              "my-0.5": nestLabel && indicator === "dashed",
              "w-0 border-[1.5px] border-dashed bg-transparent": indicator === "dashed",
              "w-1": indicator === "line"
            })} style={indicatorStyle} />}<div className={cn("flex flex-1 justify-between leading-none", nestLabel ? "items-end" : "items-center")}><div className="grid gap-1.5">{nestLabel ? tooltipLabel : null}<span className="text-muted-foreground">{itemConfig_0?.label || item_0.name}</span></div>{item_0.value && <span className="font-mono font-medium text-foreground tabular-nums">{item_0.value.toLocaleString()}</span>}</div></>}</div>;
      };
      $[30] = color;
      $[31] = config;
      $[32] = formatter;
      $[33] = hideIndicator;
      $[34] = indicator;
      $[35] = nameKey;
      $[36] = nestLabel;
      $[37] = tooltipLabel;
      $[38] = t8;
    } else {
      t8 = $[38];
    }
    t7 = payload.map(t8);
    $[20] = color;
    $[21] = config;
    $[22] = formatter;
    $[23] = hideIndicator;
    $[24] = indicator;
    $[25] = nameKey;
    $[26] = nestLabel;
    $[27] = payload;
    $[28] = tooltipLabel;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  let t8;
  if ($[39] !== t7) {
    t8 = <div className="grid gap-1.5">{t7}</div>;
    $[39] = t7;
    $[40] = t8;
  } else {
    t8 = $[40];
  }
  let t9;
  if ($[41] !== t5 || $[42] !== t6 || $[43] !== t8) {
    t9 = <div className={t5} data-sentry-component="ChartTooltipContent" data-sentry-source-file="chart.tsx">{t6}{t8}</div>;
    $[41] = t5;
    $[42] = t6;
    $[43] = t8;
    $[44] = t9;
  } else {
    t9 = $[44];
  }
  return t9;
}
const ChartLegend = RechartsPrimitive.Legend;
function ChartLegendContent(t0) {
  const $ = _c(15);
  const {
    className,
    hideIcon: t1,
    nameKey,
    payload,
    verticalAlign: t2
  } = t0;
  const hideIcon = t1 === undefined ? false : t1;
  const verticalAlign = t2 === undefined ? "bottom" : t2;
  const {
    config
  } = useChart();
  if (!payload?.length) {
    return null;
  }
  const t3 = verticalAlign === "top" ? "pb-3" : "pt-3";
  let t4;
  if ($[0] !== className || $[1] !== t3) {
    t4 = cn("flex items-center justify-center gap-4", t3, className);
    $[0] = className;
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== config || $[4] !== hideIcon || $[5] !== nameKey || $[6] !== payload) {
    let t6;
    if ($[8] !== config || $[9] !== hideIcon || $[10] !== nameKey) {
      t6 = item => {
        const key = `${nameKey || item.dataKey || "value"}`;
        const itemConfig = getPayloadConfigFromPayload(config, item, key);
        return <div className={cn("flex items-center gap-1.5 [&>svg]:h-3 [&>svg]:w-3 [&>svg]:text-muted-foreground")} key={item.value}>{itemConfig?.icon && !hideIcon ? <itemConfig.icon /> : <div className="h-2 w-2 shrink-0 rounded-[2px]" style={{
            backgroundColor: item.color
          }} />}{itemConfig?.label}</div>;
      };
      $[8] = config;
      $[9] = hideIcon;
      $[10] = nameKey;
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    t5 = payload.map(t6);
    $[3] = config;
    $[4] = hideIcon;
    $[5] = nameKey;
    $[6] = payload;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[12] !== t4 || $[13] !== t5) {
    t6 = <div className={t4} data-sentry-component="ChartLegendContent" data-sentry-source-file="chart.tsx">{t5}</div>;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}

// Helper to extract item config from a payload.
function getPayloadConfigFromPayload(config: ChartConfig, payload: unknown, key: string) {
  if (typeof payload !== 'object' || payload === null) {
    return undefined;
  }
  const payloadPayload = 'payload' in payload && typeof payload.payload === 'object' && payload.payload !== null ? payload.payload : undefined;

  // SAFETY: the `in` check proves `payload` has `key`; the value stays `unknown` until checked.
  const ownValue: unknown = key in payload ? payload[key as keyof typeof payload] : undefined;
  // SAFETY: the `in` check proves `payloadPayload` has `key`; the value stays `unknown`.
  const nestedValue: unknown = payloadPayload && key in payloadPayload ? payloadPayload[key as keyof typeof payloadPayload] : undefined;
  let configLabelKey = key;
  if (typeof ownValue === 'string') {
    configLabelKey = ownValue;
  } else if (typeof nestedValue === 'string') {
    configLabelKey = nestedValue;
  }
  return configLabelKey in config ? config[configLabelKey] : config[key];
}
export { ChartContainer, ChartLegend, ChartLegendContent, ChartStyle, ChartTooltip, ChartTooltipContent };
function _temp(t0) {
  const [, config_0] = t0;
  return config_0.theme || config_0.color;
}